import { ApiError } from "./api";

/**
 * Sends unexpected browser exceptions to the API (Réglages › Santé, admin digest), so a bug a user
 * meets is known the same day even if nobody reports it. API errors are not sent: the server
 * already records its own failures, and 4xx answers are expected refusals.
 */
const MAX_PER_PAGE = 10;
const sent = new Set<string>();

function expected(error: unknown): boolean {
  if (error instanceof ApiError) return true;
  if (error && typeof error === "object" && ("isNotFound" in error || "isRedirect" in error)) return true;
  const message = error instanceof Error ? error.message : String(error);
  // Browser noise that is not a bug of the application. A page transition is aborted by the
  // browser when the tab is hidden or another navigation starts: the page itself still changes.
  return /ResizeObserver loop|Failed to fetch|NetworkError|Load failed|dynamically imported module|AbortError|Transition was aborted|Transition was skipped|Skipping view transition/i.test(
    message,
  );
}

export function reportError(error: unknown) {
  if (expected(error)) return;
  send(error);
}

function send(error: unknown) {
  if (sent.size >= MAX_PER_PAGE) return;
  const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  if (sent.has(message)) return;
  sent.add(message);
  const stack = error instanceof Error ? error.stack : undefined;
  void fetch("/api/client-errors", {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    keepalive: true,
    body: JSON.stringify({ message, stack, url: location.href }),
  }).catch(() => undefined);
}

const RELOADED_KEY = "erp.chunkReloadAt";

/**
 * A code chunk of the release this tab was opened with is gone after a deploy: reload once onto
 * the new release (not in a loop if the chunk is really missing), and tell Santé either way.
 */
function onChunkLoadFailure(event: Event & { payload?: unknown }) {
  send(new Error(`Code chunk failed to load: ${event.payload instanceof Error ? event.payload.message : "unknown"}`));
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(RELOADED_KEY) ?? 0);
  } catch {
    // Storage refused (private mode): reload anyway, the guard below still limits it to once a minute.
  }
  if (Date.now() - last < 60_000) return;
  event.preventDefault();
  try {
    sessionStorage.setItem(RELOADED_KEY, String(Date.now()));
  } catch {
    // Same as above.
  }
  location.reload();
}

export function installErrorReporting() {
  window.addEventListener("vite:preloadError", onChunkLoadFailure);
  window.addEventListener("error", (event) => {
    // Scripts of browser extensions are not ours.
    if (event.filename && !event.filename.startsWith(location.origin)) return;
    reportError(event.error ?? event.message);
  });
  window.addEventListener("unhandledrejection", (event) => reportError(event.reason));
}
